/**
 * Step 3 (RO): the offering's own form, prefilled, then Import.
 *
 * The form comes from `getMigrateRequestForm`: the offering's real parameters
 * with the legacy description/system and the customer (in the "who is this
 * for" field) already checked server-side by `request_prefill`. It renders
 * with the same `OfferingParameterField` the request forms elsewhere use, and
 * the answers go to `importLegacyTicket`, which validates them the way
 * `prepareServiceRequest` does before anything is filed.
 */

import { Alert, Button, Field, Form, Input, Textarea } from "@norican/react";
import { useMemo, useState } from "react";

import {
  ErrorState,
  FieldHighlightLegend,
  HighlightedField,
  LoadingState,
  SectionCard,
} from "@/components/common";
import {
  answerFor,
  type AnswerMap,
  isAnswerMissing,
  isParameterVisible,
  OfferingParameterField,
  seedLookupAnswers,
  withCurrentRequirement,
} from "@/features/tickets/components/offering-parameter-fields";
import { applyPrefill } from "@/features/tickets/lib/offering-prefill";
import { useManagerAutofill } from "@/features/tickets/lib/use-manager-autofill";
import { useLanguage, useT } from "@/i18n";
import type {
  AssistantRequestFormEvent,
  LegacySummary,
  MigrateImportRequest,
  MigrateImportResponse,
  OfferingParameter,
} from "@/lib/api";
import { useFieldHighlight } from "@/lib/field-highlight";

import { useImportLegacyTicket, useMigrateRequestForm } from "../api";
import {
  buildImportBody,
  type Classification,
  type Customer,
  DESCRIPTION_MAX_LENGTH,
  type ImportAnswer,
  isUploadWaived,
  legacyDescription,
  legacySubject,
  SUBJECT_MAX_LENGTH,
  SUBJECT_MIN_LENGTH,
} from "../lib/migrate-helpers";
import { AttachmentsSection } from "./attachments-section";

export interface ReviewRequestProps {
  summary: LegacySummary;
  classification: Classification;
  customer: Customer;
  onBack: () => void;
  onDone: (result: MigrateImportResponse) => void;
}

export function ReviewRequest(props: ReviewRequestProps) {
  const { t } = useT("tickets");
  const form = useMigrateRequestForm(
    props.summary.legacy_number,
    props.classification.offeringId,
    props.customer.email,
  );

  if (form.isPending) {
    return <LoadingState />;
  }
  if (form.isError || form.data === undefined) {
    return (
      <ErrorState
        error={form.error}
        onRetry={() => void form.refetch()}
        actions={
          <Button variant="outline" tone="neutral" onClick={props.onBack}>
            {t("migrate.back")}
          </Button>
        }
      />
    );
  }
  return <RequestForm key={form.data.offering_id} form={form.data} {...props} />;
}

function visibleParameters(
  parameters: OfferingParameter[],
  answers: AnswerMap,
): OfferingParameter[] {
  return [...parameters]
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .filter((parameter) => isParameterVisible(parameter, answers))
    .map((parameter) => withCurrentRequirement(parameter, answers));
}

function RequestForm({
  form,
  summary,
  classification,
  customer,
  onBack,
  onDone,
}: ReviewRequestProps & { form: AssistantRequestFormEvent }) {
  const { t } = useT("tickets");
  const { language } = useLanguage();
  const parameters = useMemo(() => form.parameters ?? [], [form]);
  const [initial] = useState(() =>
    applyPrefill(parameters, seedLookupAnswers(parameters), form.prefill),
  );
  const [answers, setAnswers] = useState<AnswerMap>(initial.answers);
  const [prefilled, setPrefilled] = useState<ReadonlySet<string>>(
    () => new Set(initial.prefilledIds),
  );
  // What the legacy ticket filled in, kept for the field-state tint (7 Oct).
  const [legacy] = useState(() => ({
    subject: legacySubject(summary),
    description: legacyDescription(summary),
  }));
  const [subject, setSubject] = useState(legacy.subject);
  const [description, setDescription] = useState(legacy.description);
  const subjectState = useFieldHighlight(legacy.subject, subject, true);
  const descriptionState = useFieldHighlight(legacy.description, description, true);
  const [missing, setMissing] = useState<string[]>([]);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [attachmentIds, setAttachmentIds] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  // A fresh key per attempt that reached the server and was rejected: a
  // rejection writes nothing, and the corrected form is a new request rather
  // than a replay of the refused one.
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const importTicket = useImportLegacyTicket();
  const managerAutofill = useManagerAutofill(parameters, setAnswers);

  // An offering's required upload field is waived when the migration brings
  // a file that will really be attached — dropped in below, or a legacy file
  // the attachment policy allows — because those go onto the request as a
  // whole. The server decides again at import (it counts what actually
  // downloaded); if it still asks for the file, the field turns required here
  // until the dropped files change.
  const [serverWantsUpload, setServerWantsUpload] = useState(false);
  const waiveUpload = isUploadWaived(summary, attachmentIds, serverWantsUpload);
  const formParameters = useMemo(
    () =>
      waiveUpload
        ? parameters.map((parameter) =>
            parameter.type === "ATTACHMENT" ? { ...parameter, required: false } : parameter,
          )
        : parameters,
    [parameters, waiveUpload],
  );
  const visible = visibleParameters(formParameters, answers);
  const subjectOk = subject.trim().length >= SUBJECT_MIN_LENGTH;
  const descriptionOk = description.trim().length > 0;
  const busy = importTicket.isPending || uploading;

  const handleImport = () => {
    const nextMissing = visible
      .filter((parameter) => isAnswerMissing(parameter, answerFor(answers, parameter.parameter_id)))
      .map((parameter) => parameter.parameter_id);
    setMissing(nextMissing);
    if (nextMissing.length > 0 || !subjectOk || !descriptionOk || busy) {
      return;
    }
    const payloadAnswers: ImportAnswer[] = visible.map((parameter) => {
      const answer = answerFor(answers, parameter.parameter_id);
      if (parameter.type === "ATTACHMENT") {
        return { parameter_id: parameter.parameter_id, attachment_ids: answer.attachmentIds ?? [] };
      }
      return {
        parameter_id: parameter.parameter_id,
        value: answer.value,
        value_ids: answer.valueIds,
        attachment_ids: [],
      };
    });
    const body = buildImportBody({
      legacyNumber: summary.legacy_number,
      classification,
      customer,
      subject,
      description,
      language,
      answers: payloadAnswers,
      attachmentIds,
    }) as MigrateImportRequest;
    importTicket.mutate(
      { body, idempotencyKey },
      {
        onSuccess: (result) => {
          if (result.status === "rejected") {
            setIdempotencyKey(crypto.randomUUID());
            if ((result.field_errors ?? []).some((error) => error.code === "ATTACHMENT_REQUIRED")) {
              setServerWantsUpload(true);
            }
            setServerErrors(
              Object.fromEntries(
                (result.field_errors ?? []).map((error) => [error.parameter_id, error.code]),
              ),
            );
            return;
          }
          onDone(result);
        },
      },
    );
  };

  const clearFieldError = (parameterId: string) => {
    setMissing((previous) =>
      previous.includes(parameterId) ? previous.filter((id) => id !== parameterId) : previous,
    );
    setServerErrors((previous) => {
      if (previous[parameterId] === undefined) {
        return previous;
      }
      const rest = { ...previous };
      delete rest[parameterId];
      return rest;
    });
  };

  // Worked out at render time, not only on the field's own change: a field can
  // stop being required without being touched (a file dropped into the
  // Attachments section waives the upload field), and a stale error would keep
  // the form from submitting (the DS Form refuses while any field is invalid).
  const errorFor = (parameterId: string): string | undefined => {
    const parameter = formParameters.find((p) => p.parameter_id === parameterId);
    const stillMissing =
      parameter !== undefined && isAnswerMissing(parameter, answerFor(answers, parameterId));
    if (missing.includes(parameterId) && stillMissing) {
      return t("validation.fieldRequired");
    }
    const code = serverErrors[parameterId];
    if (code === undefined || (code === "ATTACHMENT_REQUIRED" && !stillMissing)) {
      return undefined;
    }
    return t("migrate.review.fieldInvalid");
  };

  const handleAttachmentIdsChange = (ids: string[]) => {
    // New files: let the server judge the waiver again on the next Import.
    setServerWantsUpload(false);
    setAttachmentIds(ids);
  };

  return (
    <Form className="flex flex-col gap-6" onFormSubmit={handleImport}>
      <SectionCard title={t("migrate.review.titleRO")} description={form.offering_name}>
        <div className="flex flex-col gap-4">
          <FieldHighlightLegend />
          <HighlightedField state={subjectState}>
            <Field
              name="subject"
              label={t("migrate.review.subject")}
              error={subjectOk ? undefined : t("migrate.review.subjectRequired")}
              required
            >
              <Input
                name="subject"
                value={subject}
                maxLength={SUBJECT_MAX_LENGTH}
                onChange={(event) => setSubject(event.target.value)}
              />
            </Field>
          </HighlightedField>
          <HighlightedField state={descriptionState}>
            <Field
              name="description"
              label={t("migrate.review.description")}
              error={descriptionOk ? undefined : t("migrate.review.descriptionRequired")}
              required
            >
              <Textarea
                name="description"
                value={description}
                maxLength={DESCRIPTION_MAX_LENGTH}
                rows={6}
                autoResize
                onChange={(event) => setDescription(event.target.value)}
              />
            </Field>
          </HighlightedField>
          {Object.keys(serverErrors).length > 0 ? (
            <Alert tone="danger">{t("migrate.review.fieldErrors")}</Alert>
          ) : null}
          {visible.map((parameter) => (
            <OfferingParameterField
              key={parameter.parameter_id}
              parameter={parameter}
              answer={answerFor(answers, parameter.parameter_id)}
              error={errorFor(parameter.parameter_id)}
              autoFilled={managerAutofill.isAutoFilled(parameter.parameter_id)}
              prefilled={prefilled.has(parameter.parameter_id)}
              onEmployeeSelect={(employee) => {
                clearFieldError(parameter.parameter_id);
                managerAutofill.fillFrom(parameter.parameter_id, employee, answers);
              }}
              onChange={(next) => {
                // Clear this field's error the moment it changes. The form
                // refuses to submit while any field is marked invalid, so an
                // error that only cleared on the next Import could never clear.
                clearFieldError(parameter.parameter_id);
                managerAutofill.noteEdit(parameter.parameter_id, next);
                if (prefilled.has(parameter.parameter_id)) {
                  setPrefilled((previous) => {
                    const rest = new Set(previous);
                    rest.delete(parameter.parameter_id);
                    return rest;
                  });
                }
                setAnswers((previous) => ({ ...previous, [parameter.parameter_id]: next }));
              }}
            />
          ))}
        </div>
      </SectionCard>

      <AttachmentsSection
        summary={summary}
        onAttachmentIdsChange={handleAttachmentIdsChange}
        onPendingChange={setUploading}
        disabled={importTicket.isPending}
      />

      {importTicket.isError ? <ErrorState variant="inline" error={importTicket.error} /> : null}

      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button type="button" variant="ghost" tone="neutral" onClick={onBack} disabled={busy}>
          {t("migrate.back")}
        </Button>
        <Button type="submit" loading={busy} disabled={busy || !subjectOk || !descriptionOk}>
          {importTicket.isPending ? t("migrate.review.importing") : t("migrate.review.import")}
        </Button>
      </div>
    </Form>
  );
}
